/* DC Tickets Block loader (block 5). Same skeleton as dc-sponsors.js: lazy-loads the
   self-hosted Spline runtime and scene once a scene URL is set, fades the poster, fires
   pauses off-screen, tears down on unload and on a breakpoint change,
   and waits for the hero to arm (or 4 s) before it starts watching. On top of that it
   pushes the four ticket prices into the scene's string variables, listens for the
   Buy <Tier> sensors (mouseDown on the plates) and buys: add-to-cart plus the theme's
   cart drawer when a variant id is set, else the tier's buy link. The HTML grid's forms
   use the same add-to-cart path, so a keyboard buy and a scene buy behave alike.
   MOTION lives here too: the exported scene's states are all empty (each one stores the
   base value), so its own rise, sway, hover and flip never run. This file drives them
   from a single rAF loop by writing the runtime objects directly. */
(() => {
  const MOBILE = '(max-width: 767px)';
  const TIERS = {
    general: { variable: 'priceGeneral', sensor: 'Buy General', obj: 'General', x: -660 },
    kids: { variable: 'priceKids', sensor: 'Buy Kids', obj: 'Kids', x: -220 },
    under12: { variable: 'priceUnder12', sensor: 'Buy Under12', obj: 'Under12', x: 220 },
    early: { variable: 'priceEarly', sensor: 'Buy Early', obj: 'Early', x: 660 }
  };
  const ORDER = ['general', 'kids', 'under12', 'early'];
  /* Motion constants. The scene's own states export EMPTY (every state holds the base
     value), so the flip, hover, rise and sway are driven from here instead. Base values
     read from the scene: Rise <Tier> y -80, Ticket <Tier> rotation (-3, 0, 2) degrees. */
  const RISE_FROM = -80, RISE_MS = 700, RISE_STAGGER = 120;
  const SWAY_DEG = 3, SWAY_MS = 3400, SWAY_STEP = 400;
  const LIFT_U = 20, LIFT_MS = 250;
  const FLIP_MS = 600;
  const DEG = Math.PI / 180;
  const SENSOR_RE = /^Buy (General|Kids|Under12|Early)$/;
  /* Pushed into the scene's price variables (the 92 u front pill fits SOLD, not SOLD OUT). */
  const SOLD_OUT = 'SOLD';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const saveData = navigator.connection && navigator.connection.saveData;

  /* The live scene is WebGPU only, for the same reason as the sponsors block: the card
     artwork in this export is texture the runtime's WebGL path does not build, so on WebGL
     every face draws as a flat grey block with a grey band across the stage (measured on a
     real GPU, runtime 2.0.27 and 2.0.33 alike). A device without a WebGPU adapter keeps
     the HTML grid, which is the fallback this block already carries for no-JS and reduced
     motion, and never loads the runtime. An adapter is requested rather than trusting
     navigator.gpu, which Chrome exposes on blocklisted GPUs and then hands back null. */
  const gpuReady = async () => {
    try { return !!(navigator.gpu && await navigator.gpu.requestAdapter()); } catch (e) { return false; }
  };

  /* Telling a tap from a swipe.

     dc-event lets a phone scroll over these canvases by hiding touchmove from the Spline
     runtime, which leaves this file with the other half of the problem: the runtime still
     reports a mouseDown, so the thumb that starts a scroll on a card would turn it over,
     and on a Buy plate would add a ticket to the cart on the way past.

     The runtime gives no way to ask whether a mouseDown came from a finger or a mouse, so
     the gesture is tracked here. While a touch is down the action is held rather than run,
     and it runs on the release, and only if no swipe was recorded in between. A mouse
     never sets touch.live, so it keeps acting on the press with no delay. The immediate
     path is guarded too, in case a runtime reports the press at the end of the gesture
     rather than the start.

     POINTER events, not touch. The runtime reports its mouseDown off pointerdown, and a
     browser fires pointerdown BEFORE touchstart, so a touchstart listener sets the flag
     one event too late: measured, a swipe that began on a card turned it over anyway
     while a swipe that began between cards behaved. On window with capture, so this is
     ahead of the runtime's own listener whichever node it chose (this file is parsed long
     before boot() imports the runtime, and capture listeners on a node run in
     registration order). */
  const SWIPE_GRACE = 400;
  const touch = { live: false, pending: null };
  const swiping = () => {
    const api = window.__dcEvent;
    return !!(api && api.swipedAt && performance.now() - api.swipedAt < SWIPE_GRACE);
  };
  const CAP = { capture: true, passive: true };
  window.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.live = true; touch.pending = null;
  }, CAP);
  window.addEventListener('pointerup', () => {
    const run = touch.pending;
    touch.live = false; touch.pending = null;
    if (run && !swiping()) run();
  }, CAP);
  window.addEventListener('pointercancel', () => { touch.live = false; touch.pending = null; }, CAP);

  const cards = (root) => Array.from(root.querySelectorAll('[data-dc-card]'));
  const cardFor = (root, tier) => root.querySelector('[data-dc-card][data-tier="' + tier + '"]');
  const isSold = (card) => card.dataset.soldOut === 'true';
  const priceOf = (card) => (isSold(card) ? SOLD_OUT : (card.dataset.price || '').trim());

  /* Prices live in Shopify (section settings), never in the scene. */
  const pushPrices = (app, root) => {
    cards(root).forEach((card) => {
      const t = TIERS[card.dataset.tier];
      if (!t) return;
      try { app.setVariable(t.variable, priceOf(card)); } catch (e) {}
    });
  };

  /* Cart: Horizon's drawer re-renders its cart-items-component from the `sections`
     hash of a cart:update event (event.detail.data.sections[sectionId]) and the
     header's cart-icon reads event.detail.data.itemCount. cart-drawer-component
     exposes open(). Anything missing falls through to the cart page. */
  const cartSectionIds = () => {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id && ids.indexOf(id) < 0) ids.push(id);
    });
    return ids;
  };

  const countFromSections = (sections, ids) => {
    if (!sections) return null;
    for (let i = 0; i < ids.length; i += 1) {
      const html = sections[ids[i]];
      if (!html) continue;
      try {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
        if (el) { const n = parseInt(el.textContent, 10); if (!Number.isNaN(n)) return n; }
      } catch (e) {}
    }
    return null;
  };

  const openDrawer = (root, data, count) => {
    const drawer = document.querySelector('cart-drawer-component');
    const item = data && data.items && data.items[0];
    const detail = {
      resource: data,
      sourceId: 'dc-tickets',
      data: { source: 'dc-tickets', itemCount: count, productId: item && item.product_id, sections: (data && data.sections) || {} }
    };
    try { document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail })); } catch (e) {}
    if (drawer) {
      try { if (typeof drawer.open === 'function') { drawer.open(); return true; } } catch (e) {}
      try { if (typeof drawer.showDialog === 'function') { drawer.showDialog(); return true; } } catch (e) {}
    }
    location.href = root.dataset.cartUrl || '/cart';
    return false;
  };

  const addToCart = async (root, card) => {
    const id = parseInt(card.dataset.variantId, 10);
    if (!id) return false;
    const ids = cartSectionIds();
    const body = { items: [{ id, quantity: 1 }] };
    if (ids.length) { body.sections = ids.join(','); body.sections_url = location.pathname; }
    const addUrl = (root.dataset.cartAddUrl || '/cart/add') + '.js';
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || ('cart add ' + res.status));
    let count = countFromSections(data.sections, ids);
    if (count === null) {
      try {
        const cart = await (await fetch((root.dataset.cartUrl || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })).json();
        if (cart && typeof cart.item_count === 'number') count = cart.item_count;
      } catch (e) {}
    }
    openDrawer(root, data, count === null ? 1 : count);
    return true;
  };

  const buy = (root, tier) => {
    const card = cardFor(root, tier);
    if (!card || isSold(card) || root.__dcBusy) return;
    if (card.dataset.variantId) {
      root.__dcBusy = true;
      root.classList.add('is-adding'); card.classList.add('is-adding');
      addToCart(root, card)
        .catch(() => { location.href = card.dataset.buyUrl || root.dataset.cartUrl || '/cart'; })
        .finally(() => { root.__dcBusy = false; root.classList.remove('is-adding'); card.classList.remove('is-adding'); });
    } else if (card.dataset.buyUrl) {
      location.href = card.dataset.buyUrl;
    }
  };

  const bindForms = (root) => {
    root.querySelectorAll('[data-dc-form]').forEach((form) => {
      if (form.__dcBound) return;
      form.__dcBound = true;
      form.addEventListener('submit', (e) => {
        const card = form.closest('[data-dc-card]');
        if (!card) return;
        e.preventDefault();
        buy(root, card.dataset.tier);
      });
    });
  };


  /* ---- Buy hit test ---------------------------------------------------------------
     The Buy plate is a child of the card Empty, whose Object-mode hit test swallows it:
     every pointer down on a card reports the parent ("Ticket <Tier>") and the plate's own
     sensor never surfaces. So the plate is located geometrically instead. The runtime
     exposes the real three.js scene and camera, so the plate's four corners are projected
     to screen space and the pointer is tested against that quad. */
  const m4apply = (v, e) => {
    const x = v[0], y = v[1], z = v[2];
    const w = 1 / (e[3] * x + e[7] * y + e[11] * z + e[15]);
    return [(e[0] * x + e[4] * y + e[8] * z + e[12]) * w,
            (e[1] * x + e[5] * y + e[9] * z + e[13]) * w,
            (e[2] * x + e[6] * y + e[10] * z + e[14]) * w];
  };

  const find3D = (app, name) => {
    let found = null;
    try { app._scene.traverse((o) => { if (!found && o.name === name) found = o; }); } catch (e) {}
    return found;
  };

  /* Screen-space bounding box of a plate, from its four local corners. */
  const plateBox = (app, obj, halfW, halfH, rect, padScale) => {
    const cam = app._camera;
    if (!obj || !cam || !rect.width) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const corners = [[-halfW, -halfH, 0], [halfW, -halfH, 0], [halfW, halfH, 0], [-halfW, halfH, 0]];
    for (let i = 0; i < 4; i += 1) {
      const world = m4apply(corners[i], obj.matrixWorld.elements);
      const view = m4apply(world, cam.matrixWorldInverse.elements);
      if (view[2] > 0) return null; /* behind the camera */
      const ndc = m4apply(view, cam.projectionMatrix.elements);
      const sx = (ndc[0] * 0.5 + 0.5) * rect.width;
      const sy = (-ndc[1] * 0.5 + 0.5) * rect.height;
      if (sx < minX) minX = sx; if (sx > maxX) maxX = sx;
      if (sy < minY) minY = sy; if (sy > maxY) maxY = sy;
    }
    /* a touch of slop so a near miss on a phone still buys */
    const px = padScale === undefined ? 0.12 : padScale;
    const py = padScale === undefined ? 0.25 : padScale;
    const padX = (maxX - minX) * px, padY = (maxY - minY) * py;
    return { minX: minX - padX, maxX: maxX + padX, minY: minY - padY, maxY: maxY + padY };
  };

  /* ---- Card faces ----------------------------------------------------------------
     The words on a ticket are DOM, not scene geometry.

     A card lands at roughly 248 x 387 css px on a 1600 px desktop, a scale of about
     0.64 against its 386 x 643 authored size. At that scale the scene's TAP TO TURN
     was 7.4 world units tall, so it rendered near 4.5 real pixels, and the back copy
     was a single baked plane 340 units wide inside a 386 unit card, which could not be
     scaled past 13 percent without hanging off the ticket. Neither is fixable in place.

     So the scene keeps the OBJECT (die-cut shape, red rim, aurora film, the photo, the
     buy plate, the rise, the sway, the flip) and gives up the WORDS. Each card gets an
     absolutely positioned face authored at the same 386 x 643, projected onto the card
     every frame with one transform. Copy comes from the HTML grid that is already in
     the page, so there is one source of truth and JJ edits every line in the theme
     editor instead of re-exporting a scene.

     The face never takes pointer events: taps still land on the canvas, so the flip and
     the buy-plate hit test are untouched. */
  const CARD_W = 386, CARD_H = 643, CARD_HALF_H = 321.5;
  /* Scene meshes the face replaces. Hidden, not removed: if a name ever stops matching
     after a re-export the scene's own copy is still there and the card is never blank.
     Blocks Top and Blocks Foot go too. They are the white and red data-block motif, they
     sit exactly where the tap hint and the identity block now land, and at this render
     size they read as broken tiles rather than as a design. */
  const FACE_HIDDEN = [
    'Tap', 'Tier', 'Price', 'Price Pill', 'Foot L', 'Foot R', 'Wordmark', 'Rule',
    'Blocks Top', 'Blocks Foot', 'Back Copy', 'Back Price', 'Back Foot', 'QR Ghost',
    /* the photo plate: a rectangle over a scalloped die-cut, it hung out of the corners;
       the DOM sheet carries the photo now, clipped by the die-cut itself */
    'Photo'
  ];

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const readText = (card, sel) => {
    const n = card.querySelector(sel);
    return n ? n.textContent.trim() : '';
  };

  /* Local point on the card plane to canvas pixels. The card's own matrix carries the
     rig offset, the sway roll, the hover lift and the mobile scale, so projecting two
     points off it recovers position, roll and scale together without unpicking any of
     them by hand. */
  const toScreen = (cam, obj, lx, ly, w, h) => {
    const m = obj.matrixWorld.elements;
    const wx = m[0] * lx + m[4] * ly + m[12];
    const wy = m[1] * lx + m[5] * ly + m[13];
    const wz = m[2] * lx + m[6] * ly + m[14];
    const iv = cam.matrixWorldInverse.elements;
    const ex = iv[0] * wx + iv[4] * wy + iv[8] * wz + iv[12];
    const ey = iv[1] * wx + iv[5] * wy + iv[9] * wz + iv[13];
    const ez = iv[2] * wx + iv[6] * wy + iv[10] * wz + iv[14];
    const pm = cam.projectionMatrix.elements;
    const cx = pm[0] * ex + pm[4] * ey + pm[8] * ez + pm[12];
    const cy = pm[1] * ex + pm[5] * ey + pm[9] * ez + pm[13];
    const cw = pm[3] * ex + pm[7] * ey + pm[11] * ez + pm[15];
    if (!cw) return null;
    return { x: (cx / cw * 0.5 + 0.5) * w, y: (0.5 - cy / cw * 0.5) * h, dist: -ez };
  };

  /* ---- Ticket art ----------------------------------------------------------------
     The whole face is drawn here now, not just the words: the photo, the film wash,
     the contour rings, the stars, the barcode and the glow. The scene keeps the object
     (die-cut body, red rim, edge, buy plate, all the motion) and this sheet lies on it.

     Why: the scene's photo was a plain 350 x 380 plate over a die-cut whose top corners
     are scalloped 62 units deep, so on every card the photo's corners hung out past the
     ticket edge, and nothing on a flat plate can follow a die-cut. The DOM sheet is
     clipped by the same outline the scene draws, so every layer on it is cut by the
     ticket edge, and the treatment (the holographic ticket JJ referenced) is CSS and
     SVG that never needs a Spline re-export.

     The outline is measured from the scene's Face geometry: a 386 x 643 rectangle with
     a 61.77 u quarter-circle cut at each corner and six 12.87 u perforation circles
     straddling the top and bottom edges (centres 2.58 u inside). DOM units, top-left
     origin, y down. */
  const TIX = { w: 386.03, h: 643.37, r: 61.77, nr: 12.87, nc: 2.58, nx: [90.08, 131.25, 172.43, 213.6, 254.78, 295.95] };
  const ticketPath = () => {
    const w = TIX.w, h = TIX.h, r = TIX.r, nr = TIX.nr, nh = Math.sqrt(nr * nr - TIX.nc * TIX.nc);
    const f = (n) => n.toFixed(2);
    const notch = (c, y) => 'L' + f(c - nh) + ',' + y + 'A' + f(nr) + ' ' + f(nr) + ' 0 1 0 ' + f(c + nh) + ',' + y;
    const notchBack = (c, y) => 'L' + f(c + nh) + ',' + y + 'A' + f(nr) + ' ' + f(nr) + ' 0 1 0 ' + f(c - nh) + ',' + y;
    const corner = (x, y) => 'A' + f(r) + ' ' + f(r) + ' 0 0 0 ' + f(x) + ',' + f(y);
    let p = 'M' + f(r) + ',0';
    TIX.nx.forEach((c) => { p += notch(c, '0'); });
    p += 'L' + f(w - r) + ',0' + corner(w, r) + 'L' + f(w) + ',' + f(h - r) + corner(w - r, h);
    TIX.nx.slice().reverse().forEach((c) => { p += notchBack(c, f(h)); });
    p += 'L' + f(r) + ',' + f(h) + corner(0, h - r) + 'L0,' + f(r) + corner(r, 0) + 'Z';
    return p;
  };
  const TIX_PATH = ticketPath();

  /* Nested contour rings around a label block, the topographic motif of the reference
     ticket. A rounded block (superellipse) offset outward ring by ring with a small
     wobble that grows with the ring, so the inner ring hugs the label and the outer ones
     drift like map contours. Catmull-Rom through 72 points per ring, drawn as one SVG
     with a holographic gradient stroke; the sheet's clip cuts the rings at the edge. */
  const contours = (cx, cy, hw, hh, rings, gap, seed) => {
    const out = [];
    for (let k = 0; k < rings; k += 1) {
      const d = k * gap, n = 72, pts = [];
      for (let j = 0; j < n; j += 1) {
        const a = (j / n) * Math.PI * 2;
        const c = Math.cos(a), s = Math.sin(a);
        const ex = Math.pow(Math.abs(c), 2 / 3.2) * Math.sign(c), ey = Math.pow(Math.abs(s), 2 / 3.2) * Math.sign(s);
        const wob = 1 + (0.02 + 0.012 * k) * Math.sin(a * 3 + seed + k * 0.7) + (0.012 + 0.008 * k) * Math.sin(a * 5 - seed * 1.3 + k);
        pts.push([cx + (hw + d) * ex * wob, cy + (hh + d) * ey * wob]);
      }
      let dstr = '';
      for (let j = 0; j < n; j += 1) {
        const p0 = pts[(j - 1 + n) % n], p1 = pts[j], p2 = pts[(j + 1) % n], p3 = pts[(j + 2) % n];
        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        if (!j) dstr += 'M' + p1[0].toFixed(1) + ',' + p1[1].toFixed(1);
        dstr += 'C' + c1[0].toFixed(1) + ',' + c1[1].toFixed(1) + ' ' + c2[0].toFixed(1) + ',' + c2[1].toFixed(1) + ' ' + p2[0].toFixed(1) + ',' + p2[1].toFixed(1);
      }
      out.push({ d: dstr + 'Z', o: Math.max(0.1, 0.6 - k * 0.075) });
    }
    return out;
  };
  let gradN = 0;
  const contourSvg = (cx, cy, hw, hh, rings, gap, seed) => {
    const id = 'dc-tix-holo-' + (gradN += 1);
    const rs = contours(cx, cy, hw, hh, rings, gap, seed);
    return '<svg class="dc-tix-contours" viewBox="0 0 386 643" aria-hidden="true"><defs><linearGradient id="' + id +
      '" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#9ecbff"/><stop offset=".3" stop-color="#e9a8ff"/><stop offset=".55" stop-color="#ffb3c1"/><stop offset=".78" stop-color="#ffe08a"/><stop offset="1" stop-color="#a6ffd9"/></linearGradient></defs><g fill="none" stroke="url(#' + id + ')">' +
      rs.map((r) => '<path d="' + r.d + '" opacity="' + r.o.toFixed(2) + '"/>').join('') + '</g></svg>';
  };
  const STAR = '<svg viewBox="0 0 10 10"><path d="M5 0C5.3 3.2 6.8 4.7 10 5 6.8 5.3 5.3 6.8 5 10 4.7 6.8 3.2 5.3 0 5 3.2 4.7 4.7 3.2 5 0Z"/></svg>';
  const starsHtml = (cls) => '<div class="dc-tix-stars' + (cls ? ' ' + cls : '') + '">' + STAR.repeat(5) + '</div>';
  /* A barcode-shaped strip, seeded per tier so the four cards differ and every load
     draws the same bars. Decoration: the real QR arrives by email. */
  const barcodeHtml = (seed) => {
    let x = 0, s = seed * 7919 + 13, out = '';
    const rnd = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
    while (x < 306) {
      const w = [1, 1, 1.6, 2.4, 3.2, 1, 4][Math.floor(rnd() * 7)], g = [1.4, 1.4, 2.2, 3.2][Math.floor(rnd() * 4)];
      if (x + w > 306) break;
      out += '<rect x="' + x.toFixed(1) + '" y="0" width="' + w + '" height="32"/>';
      x += w + g;
    }
    return '<div class="dc-tix-barcode"><svg viewBox="0 0 306 32" preserveAspectRatio="none" aria-hidden="true">' + out + '</svg></div>';
  };
  const EDGE_SVG = '<svg class="dc-tix-edge" viewBox="0 0 386.03 643.37" aria-hidden="true"><path d="' + TIX_PATH + '"/></svg>';
  /* Static art strings only (no copy goes through innerHTML; the words use textContent). */
  const artNode = (html) => { const n = el('div', 'dc-tix-art'); n.innerHTML = html; return n; };
  /* The red rim and its glow for a card that has no scene behind it (the dressed grid):
     the scene's own Rim mesh stands 5 u proud of the face, so this stroke is 10 wide
     centred on the edge and the black sheet above it covers the inner half. A stroke
     follows the notches and corner cuts evenly, where a drop-shadow pooled in them. */
  const rimSvg = () => {
    const id = 'dc-tix-glow-' + (gradN += 1);
    return '<svg class="dc-tix-rim" viewBox="0 0 386.03 643.37" aria-hidden="true"><defs><filter id="' + id + '" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="7"/></filter></defs>' +
      '<path d="' + TIX_PATH + '" fill="none" stroke="#ff2e2e" stroke-width="18" opacity=".42" filter="url(#' + id + ')"/>' +
      '<path d="' + TIX_PATH + '" fill="none" stroke="#e62020" stroke-width="10"/></svg>';
  };

  const buildFace = (card) => {
    const face = el('div', 'dc-tix-face');
    face.style.setProperty('--tix-path', 'path("' + TIX_PATH + '")');
    const front = el('div', 'dc-tix-face__front');
    const back = el('div', 'dc-tix-face__back');
    const fs = el('div', 'dc-tix-sheet');
    const bs = el('div', 'dc-tix-sheet');
    front.appendChild(fs);
    back.appendChild(bs);
    const sold = isSold(card);
    const price = sold ? 'Sold out' : (card.dataset.price || '').trim();
    const name = readText(card, '.dc-tickets__tier');
    const mark = card.querySelector('.dc-tickets__wordmark');
    const markSrc = mark ? mark.getAttribute('src') : '';
    const photo = card.querySelector('.dc-tickets__photo img');
    const photoSrc = photo ? (photo.currentSrc || photo.getAttribute('src')) : '';
    const seed = ORDER.indexOf(card.dataset.tier) + 1;
    const newMark = () => {
      if (!markSrc) return null;
      const img = new Image();
      img.src = markSrc; img.alt = ''; img.decoding = 'async';
      return img;
    };
    const art = artNode;

    /* front: photo window, film, rings, grain, edge, then the words */
    if (photoSrc) {
      const ph = el('div', 'dc-tix-photo');
      ph.style.backgroundImage = 'url("' + photoSrc + '")';
      fs.appendChild(ph);
    }
    fs.appendChild(el('div', 'dc-tix-wash'));
    fs.appendChild(el('div', 'dc-tix-band'));
    fs.appendChild(art(contourSvg(193, 470, 118, 56, 9, 15, 1.7 + seed * 0.4)));
    fs.appendChild(el('div', 'dc-tix-grain'));
    fs.appendChild(art(EDGE_SVG));
    fs.appendChild(el('p', 'dc-tix-face__tap', 'Tap to turn'));
    const id = el('div', 'dc-tix-face__id');
    const m1 = newMark();
    if (m1) { m1.className = 'dc-tix-face__mark'; id.appendChild(m1); }
    const idText = el('div', 'dc-tix-face__idtext');
    idText.appendChild(el('span', 'dc-tix-face__venue', readText(card, '.dc-tickets__venue')));
    idText.appendChild(el('span', 'dc-tix-face__date', readText(card, '.dc-tickets__date')));
    id.appendChild(idText);
    fs.appendChild(id);
    fs.appendChild(el('p', 'dc-tix-face__tier' + (name.length > 9 ? ' is-long' : ''), name));
    fs.appendChild(art(starsHtml('')));
    fs.appendChild(el('p', 'dc-tix-face__price' + (sold ? ' is-sold' : ''), price));
    fs.appendChild(art(barcodeHtml(seed)));
    const foot = el('div', 'dc-tix-face__foot');
    card.querySelectorAll('.dc-tickets__foot span').forEach((sp) => {
      foot.appendChild(el('span', null, sp.textContent.trim()));
    });
    fs.appendChild(foot);

    /* back: same film, rings around the price, the list, and a drawn BUY plate that sits
       exactly on the scene's plate (240 x 56 at 353.5), which is what the hit test aims at */
    bs.appendChild(el('div', 'dc-tix-wash'));
    bs.appendChild(el('div', 'dc-tix-band'));
    bs.appendChild(art(contourSvg(193, 300, 150, 90, 10, 15, 2.9 + seed * 0.4)));
    bs.appendChild(el('div', 'dc-tix-grain'));
    bs.appendChild(art(EDGE_SVG));
    bs.appendChild(el('p', 'dc-tix-face__kicker', readText(card, '.dc-tickets__kicker')));
    const lines = el('ul', 'dc-tix-face__lines');
    card.querySelectorAll('.dc-tickets__lines li').forEach((li) => {
      const t = li.textContent.trim();
      if (t) lines.appendChild(el('li', null, t));
    });
    bs.appendChild(lines);
    bs.appendChild(art(starsHtml('dc-tix-stars--back')));
    bs.appendChild(el('p', 'dc-tix-face__bprice' + (sold ? ' is-sold' : ''), price));
    const buyEl = card.querySelector('[data-dc-buy]');
    let buyLabel = 'Buy ticket';
    if (buyEl) {
      const c = buyEl.cloneNode(true);
      c.querySelectorAll('.dc-tickets__sr,.dc-tickets__chev').forEach((n) => n.remove());
      buyLabel = c.textContent.trim() || buyLabel;
    }
    bs.appendChild(el('div', 'dc-tix-buy' + (sold ? ' is-off' : ''), sold ? 'Sold out' : buyLabel));
    bs.appendChild(el('p', 'dc-tix-face__note', readText(card, '.dc-tickets__note')));
    const bid = el('div', 'dc-tix-face__bid');
    const m2 = newMark();
    if (m2) bid.appendChild(m2);
    bid.appendChild(el('span', null, readText(card, '.dc-tickets__date')));
    bs.appendChild(bid);

    /* perspective lives on the outer element and the 3D transform on this inner one.
       Putting a perspective() function in the same transform list as transform-style:
       preserve-3d left the browser free to fold the two together, and it skewed the
       whole face into a diagonal smear part way through a turn. This is the ordinary
       flip-card structure and it behaves. */
    const inner = el('div', 'dc-tix-face__card');
    inner.appendChild(front);
    inner.appendChild(back);
    face.appendChild(inner);
    return face;
  };

  /* The stage floor. Ground is a 6000 unit plane sitting under a spotlight, and the pool
     of grey it threw came up right behind the bottom of every card: it flattened the
     wordmark printed there and swallowed the ghost marquee row that runs along the same
     line. Hiding it leaves the stage black, which is what the cards want behind them.
     Nothing is lit by it (the card faces are unlit materials), so only the floor goes. */
  const blackout = (app) => {
    const ground = find3D(app, 'Ground');
    if (ground) ground.visible = false;
  };

  const faces = (app, root, parts) => {
    const stage = root.querySelector('.dc-tickets__stage');
    const canvas = root.querySelector('[data-dc-canvas]');
    if (!stage || !canvas) return null;

    /* findObjectByName hands back Spline's own proxy, which carries no matrices and
       no child meshes. find3D walks the real three.js graph, which is what both the
       projection and the visibility flags need. The motion loop keeps writing through
       the proxy; the proxy writes through to this same object. */
    parts.forEach((p) => {
      p.card3d = find3D(app, 'Ticket ' + TIERS[p.tier].obj);
      if (!p.card3d) return;
      (p.card3d.children || []).forEach((ch) => {
        if (FACE_HIDDEN.indexOf(ch.name) > -1) ch.visible = false;
      });
    });

    const layer = el('div', 'dc-tickets__faces');
    layer.setAttribute('aria-hidden', 'true');
    parts.forEach((p) => {
      const htmlCard = cardFor(root, p.tier);
      if (!htmlCard) { p.face = null; return; }
      p.face = buildFace(htmlCard);
      p.faceCard = p.face.firstElementChild;
      layer.appendChild(p.face);
    });
    stage.appendChild(layer);

    /* The canvas box is measured once and on resize, not every frame: a
       getBoundingClientRect after the previous frame's style writes is a forced layout
       per frame, and only its width and height are used, which scrolling never changes. */
    let rect = null;
    const measure = () => { rect = canvas.getBoundingClientRect(); };
    window.addEventListener('resize', measure, { passive: true });
    const rectRo = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    if (rectRo) { try { rectRo.observe(stage); } catch (e) {} }
    const update = () => {
      if (!rect) measure();
      const r = rect;
      if (!r.width || !r.height) return;
      const cam = app._camera;
      if (!cam) return;
      for (let i = 0; i < parts.length; i += 1) {
        const p = parts[i];
        if (!p.face || !p.card3d) continue;
        p.card3d.updateWorldMatrix(true, false);
        const o = toScreen(cam, p.card3d, 0, 0, r.width, r.height);
        const u = toScreen(cam, p.card3d, 0, CARD_HALF_H, r.width, r.height);
        if (!o || !u) continue;
        const dx = u.x - o.x, dy = u.y - o.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (!len) continue;
        const s = len / CARD_HALF_H;
        /* Unrotated, the up point sits directly above the centre, so this reads 0. */
        const roll = Math.atan2(dx, -dy) * 180 / Math.PI;
        const flip = (p.card3d.rotation.y || 0) * 180 / Math.PI;
        /* Camera distance in screen pixels, so the DOM flip foreshortens like the scene's.
           The vanishing point is this card's own centre, which is what the outer element
           carrying the perspective is centred on. */
        const persp = Math.max(1, o.dist * s);
        p.face.style.perspective = persp.toFixed(1) + 'px';
        p.face.style.transform = 'translate(' + o.x.toFixed(1) + 'px,' + o.y.toFixed(1) + 'px)';
        p.faceCard.style.transform =
          'rotate(' + roll.toFixed(2) + 'deg) scale(' + s.toFixed(4) + ') rotateY(' + flip.toFixed(2) + 'deg)';
        /* Which side shows is decided here, not by backface-visibility. The faces layer
           is a flat 3D context, so the card's own rotateY never gets accumulated into
           the browser's backface test for the two children and both sides stayed
           visible through the turn. The flip angle is already known, so switch on it. */
        const a = ((flip % 360) + 360) % 360;
        const back = a > 90 && a < 270;
        if (back !== p.faceBack) { p.faceBack = back; p.face.classList.toggle('is-back', back); }
        if (!p.faceOn) { p.faceOn = true; p.face.classList.add('is-on'); }
      }
    };

    return {
      update,
      destroy() {
        window.removeEventListener('resize', measure);
        try { rectRo && rectRo.disconnect(); } catch (e) {}
        try { layer.remove(); } catch (e) {}
        parts.forEach((p) => { p.face = null; p.faceCard = null; p.faceOn = false; p.faceBack = undefined; p.card3d = null; });
      }
    };
  };

  /* ---- Motion engine -------------------------------------------------------------
     One rAF loop drives every card: rise on load, idle sway, hover lift, tap to flip.
     Everything is a pose computed from elapsed time, so a dropped frame never leaves a
     card half-way. Writes go straight to the runtime objects (position/rotation), which
     is the only channel that works: the exported states carry no values. */
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const easeOutBack = (t) => { const c = 1.2; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };
  const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t);

  const motion = (app, root) => {
    const parts = [];
    ORDER.forEach((tier, i) => {
      const t = TIERS[tier];
      let rig = null, rise = null, card = null;
      try {
        rig = app.findObjectByName('Rig ' + t.obj);
        rise = app.findObjectByName('Rise ' + t.obj);
        card = app.findObjectByName('Ticket ' + t.obj);
      } catch (e) {}
      if (!rig || !rise || !card) return;
      parts.push({ tier, i, rig, rise, card, buy3d: find3D(app, 'Buy ' + t.obj), flipped: false, flipT: -1, hovered: false, hoverT: -1, hoverFrom: 0 });
    });
    if (!parts.length) return null;

    const state = { parts, start: performance.now(), raf: 0, alive: true, visible: true, hovered: null, suppressFlipUntil: 0 };
    blackout(app);
    const fx = faces(app, root, parts);
    state.faces = fx;
    const byName = (name) => { for (let i = 0; i < parts.length; i += 1) { if ('Ticket ' + TIERS[parts[i].tier].obj === name) return parts[i]; } return null; };
    state.byName = byName;

    /* The loop stops with the block. It used to run for the rest of the visit: four
       world-matrix updates, eight projections, twelve style writes and a redraw request
       every frame while the reader was three screens away. Off-screen it now ends
       itself; the observer in boot() restarts it. Rise, hover and flip are all absolute
       in time, so resuming re-bases nothing; the sway phase jumps, which is invisible. */
    const frame = (now) => {
      if (!state.alive) return;
      if (!state.visible) { state.raf = 0; return; }
      const el = now - state.start;
      for (let i = 0; i < parts.length; i += 1) {
        const p = parts[i];
        /* rise */
        const rt = clamp01((el - p.i * RISE_STAGGER) / RISE_MS);
        p.rise.position.y = RISE_FROM * (1 - easeOutCubic(rt));
        /* idle sway, starts once the card has landed */
        if (rt >= 1) {
          const period = SWAY_MS + p.i * SWAY_STEP;
          const phase = ((el - RISE_MS - p.i * RISE_STAGGER) % period) / period;
          p.rig.rotation.z = -SWAY_DEG * DEG * (0.5 - 0.5 * Math.cos(phase * Math.PI * 2));
        }
        /* hover lift */
        if (p.hoverT >= 0) {
          const ht = clamp01((now - p.hoverT) / LIFT_MS);
          const target = p.hovered ? LIFT_U : 0;
          p.card.position.y = p.hoverFrom + (target - p.hoverFrom) * easeOutCubic(ht);
          if (ht >= 1) p.hoverT = -1;
        }
        /* flip */
        if (p.flipT >= 0) {
          const ft = clamp01((now - p.flipT) / FLIP_MS);
          const from = p.flipped ? 0 : Math.PI;
          const to = p.flipped ? Math.PI : 0;
          p.card.rotation.y = from + (to - from) * easeOutBack(ft);
          if (ft >= 1) { p.card.rotation.y = to; p.flipT = -1; }
        }
      }
      if (fx) { try { fx.update(); } catch (e) {} }
      try { app.requestRender && app.requestRender(); } catch (e) {}
      state.raf = requestAnimationFrame(frame);
    };
    state.raf = requestAnimationFrame(frame);
    state.setVisible = (on) => {
      state.visible = !!on;
      if (state.visible && state.alive && !state.raf) state.raf = requestAnimationFrame(frame);
    };

    const setHover = (part) => {
      if (state.hovered === part) return;
      const now = performance.now();
      if (state.hovered) { state.hovered.hovered = false; state.hovered.hoverFrom = state.hovered.card.position.y; state.hovered.hoverT = now; }
      state.hovered = part;
      if (part) { part.hovered = true; part.hoverFrom = part.card.position.y; part.hoverT = now; }
    };
    state.setHover = setHover;

    const flip = (part) => {
      if (!part) return;
      part.flipped = !part.flipped;
      part.flipT = performance.now();
    };
    state.flip = flip;

    const canvas = root.querySelector('[data-dc-canvas]');
    if (canvas) {
      const leave = () => setHover(null);
      const track = (e) => {
        const r = canvas.getBoundingClientRect();
        state.pointer = { x: e.clientX - r.left, y: e.clientY - r.top, rect: r };
      };
      canvas.addEventListener('pointerleave', leave);
      canvas.addEventListener('pointerdown', track, true);
      canvas.addEventListener('pointermove', track, true);
      state.canvas = canvas;
      state.detach = () => {
        try { canvas.removeEventListener('pointerleave', leave); } catch (e) {}
        try { canvas.removeEventListener('pointerdown', track, true); } catch (e) {}
        try { canvas.removeEventListener('pointermove', track, true); } catch (e) {}
      };
    }
    /* Did this tap land on the flipped card's BUY plate? */
    state.hitBuy = (part) => {
      if (!part || !part.flipped || !part.buy3d || !state.pointer) return false;
      const box = plateBox(app, part.buy3d, 120, 28, state.pointer.rect);
      if (!box) return false;
      const p = state.pointer;
      return p.x >= box.minX && p.x <= box.maxX && p.y >= box.minY && p.y <= box.maxY;
    };
    return state;
  };

  /* Phones: a row of four does not fit a 9:16 stage, so the cards go 2 x 2 and the whole
     set is scaled down. The scene's Mobile states are empty like every other state, so the
     layout is applied here by hand. Numbers: the play camera sits at z 1400 with a 45 deg
     vertical field of view, so it sees 1160 world units of height; the 2 x 2 block is
     2 x (380 + 322) = 1404 local units tall, and 0.70 scale puts it at 983, about 85
     percent of the frame. The floor, the marquee rows and the kicker are pushed out of the
     way because the lower row would otherwise sit underneath the floor plane. */
  const M = {
    card: { x: 230, y: 380 }, scale: 0.7,
    ground: -1200, ghostTop: 760, ghostBottom: -760
  };
  const place = (app, name, xy) => {
    let o = null;
    try { o = app.findObjectByName(name); } catch (e) {}
    if (!o) return;
    if (typeof xy.x === 'number') o.position.x = xy.x;
    if (typeof xy.y === 'number') o.position.y = xy.y;
  };
  const layout = (app, root, isMobile) => {
    ORDER.forEach((tier, i) => {
      const t = TIERS[tier];
      place(app, 'Rig ' + t.obj, isMobile
        ? { x: (i % 2 === 0 ? -M.card.x : M.card.x), y: (i < 2 ? M.card.y : -M.card.y) }
        : { x: t.x, y: 0 });
    });
    place(app, 'Ground', { y: isMobile ? M.ground : -340 });
    place(app, 'Ghost Row Top', { y: isMobile ? M.ghostTop : 470 });
    place(app, 'Ghost Row Bottom', { y: isMobile ? M.ghostBottom : -339.2 });
    /* The kicker and subline are HTML now (.dc-tickets__stagehead), so the scene's own
       copies are switched off rather than positioned. */
    ['Kicker', 'Subline'].forEach((n) => {
      let o = null;
      try { o = app.findObjectByName(n); } catch (e) {}
      if (!o) return;
      try { if (typeof o.hide === 'function') o.hide(); else o.visible = false; } catch (e) {}
    });
    let rootObj = null;
    try { rootObj = app.findObjectByName('TICKETS ROOT'); } catch (e) {}
    if (rootObj) {
      const k = isMobile ? M.scale : 1;
      rootObj.scale.x = k; rootObj.scale.y = k; rootObj.scale.z = k;
      rootObj.position.y = 0;
    }
    try { app.requestRender && app.requestRender(); } catch (e) {}
    return rootObj;
  };

  /* The export plays through the file's personal camera, so its distance and framing are
     not knowable up front. Rather than assume, measure: project the four cards, take the
     union box on screen, and scale the root until the set fills the stage with a margin.
     Two passes are enough because scaling about the root origin is near-linear on screen. */
  /* How much of the stage the card set fills. A row of four wants room to breathe, so the
     desktop target is well under the frame; the phone's 2 x 2 block can sit closer in. */
  const FIT_DESKTOP = 0.64;
  const FIT_MOBILE = 0.86;
  /* Matrices only refresh when the renderer draws, so each pass has to wait a frame before
     it measures again. Three passes, each step clamped, converges without oscillating. */
  const measure = (app, root) => {
    const canvas = root.querySelector('[data-dc-canvas]');
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, seen = 0;
    ORDER.forEach((tier) => {
      const obj = find3D(app, 'Ticket ' + TIERS[tier].obj);
      const box = plateBox(app, obj, 198, 327, rect, 0);
      if (!box) return;
      seen += 1;
      if (box.minX < minX) minX = box.minX;
      if (box.maxX > maxX) maxX = box.maxX;
      if (box.minY < minY) minY = box.minY;
      if (box.maxY > maxY) maxY = box.maxY;
    });
    if (seen < 4) return null;
    return { rect, w: maxX - minX, h: maxY - minY, minY, maxY };
  };

  const fit = (app, root, rootObj, pass) => {
    if (!rootObj) return;
    const n = pass || 0;
    if (n > 3) return;
    requestAnimationFrame(() => {
      const m = measure(app, root);
      if (!m || !m.w || !m.h) return;
      const target = matchMedia(MOBILE).matches ? FIT_MOBILE : FIT_DESKTOP;
      let k = Math.min((m.rect.width * target) / m.w, (m.rect.height * target) / m.h);
      if (Math.abs(k - 1) < 0.02) return;
      k = Math.max(0.6, Math.min(1.6, k));
      const next = Math.max(0.2, Math.min(2.5, rootObj.scale.x * k));
      rootObj.scale.x = next; rootObj.scale.y = next; rootObj.scale.z = next;
      try { app.requestRender && app.requestRender(); } catch (e) {}
      fit(app, root, rootObj, n + 1);
    });
  };

  /* The stage header is HTML sitting over the top of the canvas, so on phones (where the
     2 x 2 block is tall) the cards can run under it. Measure the gap and slide the whole
     root down by the equivalent in world units. Local block height is known from the
     layout: 2 x (row offset + half card) on a phone, one card tall on desktop. */
  const clearHeader = (app, root, rootObj, isMobile) => {
    if (!rootObj || !isMobile) return;
    requestAnimationFrame(() => {
      const m = measure(app, root);
      const head = root.querySelector('.dc-tickets__stagehead');
      const canvas = root.querySelector('[data-dc-canvas]');
      if (!m || !head || !canvas) return;
      const hb = head.getBoundingClientRect().bottom - canvas.getBoundingClientRect().top;
      const gap = m.rect.height * 0.03;
      const delta = (hb + gap) - m.minY;
      if (delta <= 0) return;
      const localH = 2 * (M.card.y + 327);
      const pxPerWorld = m.h / (localH * rootObj.scale.y);
      if (!pxPerWorld) return;
      rootObj.position.y -= delta / pxPerWorld;
      try { app.requestRender && app.requestRender(); } catch (e) {}
    });
  };

  const boot = async (root) => {
    if (root.__dcApp || !root.dataset.scene) return;
    const canvas = root.querySelector('[data-dc-canvas]');
    if (!canvas) return;
    /* A boot that is no longer the current one stops at its next await. Crossing the
       breakpoint during a load tears down and boots again, and without this the first
       boot's continuation carried on against a disposed app: a second faces layer, a
       second loop nothing could reach, two runtimes on one canvas. Same shape as the
       hero's hall token. */
    const token = (root.__dcBootToken = (root.__dcBootToken || 0) + 1);
    const stale = () => root.__dcBootToken !== token;
    const { Application } = await import(root.dataset.runtime);
    if (stale()) return;
    const app = new Application(canvas);
    root.__dcApp = app;
    await app.load(root.dataset.scene);
    if (stale()) { try { app.dispose && app.dispose(); } catch (e) {} return; }
    pushPrices(app, root);
    const isMobile = matchMedia(MOBILE).matches;
    const rootObj = layout(app, root, isMobile);
    fit(app, root, rootObj);
    let rt = 0, ct = 0;
    const headerLater = (mob) => { clearTimeout(ct); ct = setTimeout(() => clearHeader(app, root, rootObj, mob), 260); };
    headerLater(isMobile);
    const resize = () => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        try {
          const mob = matchMedia(MOBILE).matches;
          if (rootObj) rootObj.position.y = 0;
          fit(app, root, rootObj);
          headerLater(mob);
        } catch (e) {}
      }, 200);
    };
    window.addEventListener('resize', resize);
    let ro = null;
    if ('ResizeObserver' in window) {
      try { ro = new ResizeObserver(resize); ro.observe(root.querySelector('.dc-tickets__stage') || root); } catch (e) {}
    }
    root.__dcResize = () => {
      clearTimeout(rt); clearTimeout(ct);
      window.removeEventListener('resize', resize);
      try { ro && ro.disconnect(); } catch (e) {}
    };
    const mo = motion(app, root);
    root.__dcMotion = mo;
    try {
      app.addEventListener('mouseDown', (e) => {
        const name = (e && e.target && e.target.name) || '';
        const act = () => {
          const m = SENSOR_RE.exec(name);
          if (m) {
            /* A tap on a Buy plate also hits its parent card, so hold the flip off. */
            if (mo) mo.suppressFlipUntil = performance.now() + 250;
            buy(root, m[1].toLowerCase());
            return;
          }
          if (!mo || performance.now() < mo.suppressFlipUntil) return;
          const part = mo.byName(name);
          if (!part) return;
          /* On a flipped card the BUY plate is the target; anywhere else turns it back. */
          if (mo.hitBuy(part)) { mo.suppressFlipUntil = performance.now() + 250; buy(root, part.tier); return; }
          mo.flip(part);
        };
        if (touch.live) { touch.pending = act; return; }
        if (swiping()) return;
        act();
      });
      app.addEventListener('mouseHover', (e) => {
        if (!mo) return;
        const name = (e && e.target && e.target.name) || '';
        mo.setHover(mo.byName(name));
      });
    } catch (e) {}
    root.classList.add('is-live');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        try { en.isIntersecting ? app.play() : app.stop(); } catch (e) {}
        try { mo && mo.setVisible && mo.setVisible(en.isIntersecting); } catch (e) {}
      });
    }, { threshold: 0.01 });
    io.observe(root);
    root.__dcIO = io;
    root.__dcFar = releaseWhenFar(root, () => root.__dcApp === app);
  };


  /* ---- Release when far away (touch devices) --------------------------------------
     A resident Spline runtime is 150 to 250 MB of process memory plus its GPU textures,
     and this page can hold three of them below the hero (sponsor plates, the particle
     field, the tickets). On a phone the walk back up the page then stacks the hero's own
     layers on top of all that, and iOS killed the tab: "A problem repeatedly occurred"
     on JJ's phone, 2026-09-04, scrolling up from the bottom. Measured in WebKit with an
     iPhone profile: 1.2 GB resident at the bottom, 1.5 GB by the time the top came back.
     So on a touch device a block that has been more than RELEASE_VH viewports off the
     screen for RELEASE_MS gives its runtime back and returns to its poster and its lazy
     watcher; scrolling back within the lazy margin boots it again (the scene file is
     cached, two to three seconds). Desktop pointers keep their runtimes: no memory
     ceiling to speak of there, and no reload when the reader wanders. */
  const RELEASE_VH = 1.0, RELEASE_MS = 800;
  /* Either signal will do: a coarse pointer is the phone or tablet itself, and a touch
     count catches a browser that reports a fine pointer for a touch screen. A touch
     laptop lands here too, which only costs it a reload if it scrolls back. */
  const touchDevice = () => matchMedia('(pointer: coarse)').matches || (navigator.maxTouchPoints || 0) > 0;
  const releaseWhenFar = (root, still) => {
    if (!touchDevice() || !('IntersectionObserver' in window)) return null;
    let timer = 0, near = true;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        near = en.isIntersecting;
        clearTimeout(timer); timer = 0;
        if (near) return;
        timer = setTimeout(() => {
          timer = 0;
          if (near || !still()) return;
          teardown(root);
          if (root.__dcWatch) root.__dcWatch();
        }, RELEASE_MS);
      });
    }, { rootMargin: Math.round(RELEASE_VH * 100) + '% 0px' });
    io.observe(root);
    return { disconnect() { clearTimeout(timer); timer = 0; try { io.disconnect(); } catch (e) {} } };
  };

  const teardown = (root) => {
    root.__dcBootToken = (root.__dcBootToken || 0) + 1;
    try { root.__dcFar && root.__dcFar.disconnect(); } catch (e) {}
    root.__dcFar = null;
    try { if (root.__dcMotion) { root.__dcMotion.alive = false; cancelAnimationFrame(root.__dcMotion.raf); root.__dcMotion.detach && root.__dcMotion.detach(); root.__dcMotion.faces && root.__dcMotion.faces.destroy(); } } catch (e) {}
    try { root.__dcResize && root.__dcResize(); } catch (e) {}
    root.__dcResize = null;
    root.__dcMotion = null;
    try { root.__dcIO && root.__dcIO.disconnect(); } catch (e) {}
    try { root.__dcLazy && root.__dcLazy.disconnect(); } catch (e) {}
    root.__dcLazy = null;
    try { root.__dcApp && root.__dcApp.dispose && root.__dcApp.dispose(); } catch (e) {}
    root.__dcApp = null; root.__dcIO = null; root.classList.remove('is-live');
  };

  /* ---- Static grid dressing ------------------------------------------------------
     Without a scene (no WebGPU adapter, reduced motion, save-data, or no scene URL) the
     HTML grid is what the visitor sees, and that is most iPhones until iOS 26. Each grid
     card gets the same front sheet as the live cards, scaled to the card's width, above
     its own "what you get" list and its real buy button. Without JS the plain CSS card
     stays, which is the no-JS layer it always was. */
  const dressGrid = (root) => {
    if (root.__dcDressed) return;
    root.__dcDressed = true;
    const ro = 'ResizeObserver' in window ? new ResizeObserver((entries) => {
      entries.forEach((en) => { en.target.style.setProperty('--tix-k', (en.contentRect.width / CARD_W).toFixed(4)); });
    }) : null;
    cards(root).forEach((card) => {
      if (card.querySelector('.dc-tix-static')) return;
      const face = buildFace(card);
      face.classList.add('is-on', 'is-flat');
      face.querySelectorAll('.dc-tix-face__front,.dc-tix-face__back').forEach((side) => side.insertBefore(artNode(rimSvg()), side.firstChild));
      const wrap = el('div', 'dc-tix-static');
      wrap.setAttribute('aria-hidden', 'true');
      wrap.appendChild(face);
      card.insertBefore(wrap, card.firstChild);
      card.classList.add('is-dressed');
      if (ro) ro.observe(wrap); else wrap.style.setProperty('--tix-k', (wrap.getBoundingClientRect().width / CARD_W).toFixed(4));
    });
    root.classList.add('is-dressed');
  };

  const init = (root) => {
    if (root.__dcInit) return;
    root.__dcInit = true;
    bindForms(root);
    if (!root.dataset.scene) { dressGrid(root); return; }
    if (reduced || saveData || !('IntersectionObserver' in window)) { dressGrid(root); return; }
    /* Commit to the scene layout NOW, synchronously, for the reason arm() explains: the
       document's height must not change part way down a scroll. The adapter check is
       async (measured at 35 ms after DOMContentLoaded with the CPU throttled 4x); the
       rare device without WebGPU swaps to is-static at that point, which hides the stage
       (poster, canvas, stage heading) and leaves the grid, the same state the section
       renders when it has no scene URL at all. */
    root.classList.add('is-scene');
    gpuReady().then((ok) => {
      if (!root.__dcInit) return;
      if (ok) arm(root); else { root.classList.remove('is-scene'); root.classList.add('is-static'); dressGrid(root); }
    });
  };

  const arm = (root) => {
    /* Commit to the scene layout NOW, not when the scene finishes loading.

       The HTML grid is the no-JS and reduced-motion fallback, and it used to collapse out
       of flow on `is-live` — which arrives lazily, part way down the page. That took 819
       px out of this section and 664 px out of the document IN THE MIDDLE OF A SCROLL, so
       everything below jumped and the reader's position was suddenly measured against a
       different page. That is the "it broke when I scrolled up and down", and the same
       jump on first load.

       The decision to boot is already made on this line, so the layout can settle here,
       in the first frame, once. If the boot then fails the class comes off again and the
       grid returns as the fallback it is. (The class itself is added in init(), before
       the adapter check, so it lands in the first frame.) */
    const margin = parseInt(root.dataset.rootMargin || '400', 10);
    const watch = () => {
      if (root.__dcLazy) return;
      const lazy = new IntersectionObserver((entries, obs) => {
        if (entries.some((en) => en.isIntersecting)) {
          obs.disconnect();
          boot(root).catch(() => { root.classList.remove('is-live'); root.classList.remove('is-scene'); dressGrid(root); });
        }
      }, { rootMargin: margin + 'px 0px' });
      lazy.observe(root);
      root.__dcLazy = lazy;
    };
    root.__dcWatch = watch;
    /* On a phone the sponsors block above us goes first. Both blocks sit inside the same
       lazy margin on a phone viewport, so their scenes downloaded together and this one,
       the larger, held the sponsors plates off the screen for seconds on Slow 4G. The
       sponsors block reads is-live (or is-static) when it is done, or at worst this waits
       twelve seconds. Desktop bandwidth does not need the courtesy and is left alone. */
    const watchAfterSponsors = () => {
      const sp = document.querySelector('[data-dc-sponsors]');
      const done = () => !sp || sp.classList.contains('is-live') || sp.classList.contains('is-static');
      /* A reader already AT this block (a rail jump straight to TICKETS, or a thumb that
         kept going) is not made to wait for a sponsors block that may never have entered
         its own margin. "At" means the block's top is in the upper half of the viewport;
         the lazy margin is deliberately not used here, because on a phone this block is
         inside its own margin the whole time the sponsors block is on screen, which is
         exactly the moment the sponsors block needs the bandwidth to itself. */
      const near = () => root.getBoundingClientRect().top < window.innerHeight * 0.5;
      if (!matchMedia(MOBILE).matches || done() || near()) { watch(); return; }
      let fired = false;
      const onScroll = () => { if (near()) fire(); };
      const fire = () => {
        if (fired) return;
        fired = true;
        try { spMo.disconnect(); } catch (e) {}
        window.removeEventListener('scroll', onScroll);
        watch();
      };
      const spMo = new MutationObserver(() => { if (done()) fire(); });
      spMo.observe(sp, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('scroll', onScroll, { passive: true });
      /* Safety net only: a reader who reaches this block is caught by the scroll check
         long before this fires, and a Slow 4G sponsors load takes twelve seconds or more
         on its own, so a short cap here just put the two downloads back on top of each
         other (measured at 12 s: the tickets scene started 4 s before the plates landed). */
      setTimeout(fire, 20000);
    };
    /* A hero above us pins itself once its runtime arms, which pushes this block out of
       the lazy margin. Wait for the arm (or 4 s), then watch. */
    const hero = document.querySelector('.dc-hero');
    const heroPending = hero && !(hero.__dcHero && hero.__dcHero.armed) && !hero.classList.contains('is-motion');
    if (heroPending) {
      let done = false;
      const go = () => { if (done) return; done = true; document.removeEventListener('dc:hero:armed', go); requestAnimationFrame(() => setTimeout(watchAfterSponsors, 300)); };
      document.addEventListener('dc:hero:armed', go);
      setTimeout(go, 4000);
    } else watchAfterSponsors();
    let wasMobile = matchMedia(MOBILE).matches;
    matchMedia(MOBILE).addEventListener('change', (e) => {
      if (e.matches === wasMobile) return;
      wasMobile = e.matches;
      if (root.__dcApp) { teardown(root); boot(root).catch(() => {}); }
    });
  };

  const initAll = (scope) => (scope || document).querySelectorAll('[data-dc-tickets]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-dc-tickets]').forEach(teardown));
  }
})();
